import { Controller } from "stimulus"
import CookieService from "../lib/cookie"
import M from "../materialize"
import { reviewFrame } from "../review-frame/snapshot"

export default class BlogModalController extends Controller {

  modal = null
  hasShown = false
  scrollThreshold = 0.2 // 20% of content
  contentHeight = 0
  windowHeight = 0

  static get targets() {
    return ['modal']
  }

  static get values() {
    return {
      threshold: Number,
      cookieName: String,
      cookieDays: Number,
      contentSelector: String
    }
  }

  initialize() {
    // Set defaults if not provided
    if (!this.hasThresholdValue) this.thresholdValue = 0.2
    if (!this.hasCookieNameValue) this.cookieNameValue = 'blog-modal-shown'
    if (!this.hasCookieDaysValue) this.cookieDaysValue = 7
    if (!this.hasContentSelectorValue) this.contentSelectorValue = '.article-body'
    
    // Check if modal has been shown recently
    this.hasShown = CookieService.getCookie(this.cookieNameValue) === 'true'
  }

  connect() {    
    // In the translation review app the app opens the popup on purpose.
    if (reviewFrame) return
    // Add a small delay to ensure DOM is fully rendered
    setTimeout(() => {
      const modalElement = this.element.querySelector('[data-blog-modal-target="modal"]');
      
      if (!this.hasShown && modalElement) {
        // Initialize the modal with Materialize CSS
        this.modal = M.Modal.init(modalElement, {
          dismissible: true,
          opacity: 0.5,
          inDuration: 250,
          outDuration: 250
        })
        this.calculateContentHeight()
        // Only bind scroll listener if we have content to track
        if (this.contentHeight > 0) {
          this.bindScrollListener()
        }
      }
    }, 100)
  }

  calculateContentHeight() {
    // Find the main article content using configurable selector
    const articleBody = document.querySelector(this.contentSelectorValue)
    if (articleBody) {
      this.contentHeight = articleBody.scrollHeight
      this.windowHeight = window.innerHeight
    } else {
      // Fallback: use document body if specific selector not found
      this.contentHeight = document.body.scrollHeight
      this.windowHeight = window.innerHeight
    }
  }

  bindScrollListener() {
    this.checkScrollBound = this.checkScroll.bind(this)
    window.addEventListener('scroll', this.checkScrollBound, { passive: true })
  }

  checkScroll() {
    if (this.hasShown) {
      return
    }

    const scrollTop = window.pageYOffset || document.documentElement.scrollTop
    const denominator = this.contentHeight - this.windowHeight
    let scrollPercentage
    if (denominator <= 0) {
      // If content fits in the viewport or is shorter, treat as fully scrolled
      scrollPercentage = 1
    } else {
      scrollPercentage = scrollTop / denominator
    }

    if (scrollPercentage >= this.thresholdValue) {
      this.showModal()
    }
  }

  showModal() {
    if (this.hasShown || !this.modal) {
      return
    }

    this.hasShown = true
    this.modal.open()
    
    // Set cookie to prevent showing again
    CookieService.setCookie(this.cookieNameValue, 'true', this.cookieDaysValue)
    
    // Remove scroll listener to avoid unnecessary processing
    window.removeEventListener('scroll', this.checkScrollBound)
  }

  disconnect() {
    if (this.modal) {
      this.modal.destroy()
    }
    if (this.checkScrollBound) {
      window.removeEventListener('scroll', this.checkScrollBound)
    }
  }
}